import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Lock, Loader2, Truck, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';

const Checkout = () => {
  const { cart, totalPrice } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', address: '', city: '', postal_code: '',
  });

  useEffect(() => {
    if (user && typeof user === 'object') {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || user.name || '',
        email: f.email || user.email || '',
      }));
    }
  }, [user]);

  const shipping = totalPrice >= 50 || totalPrice === 0 ? 0 : 5.99;
  const total = totalPrice + shipping;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const required = ['full_name', 'email', 'address', 'city', 'postal_code'];
  const isValid = required.every((k) => form[k].trim()) && /\S+@\S+\.\S+/.test(form.email);

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-black text-[#262626] mb-2">Votre panier est vide</h1>
        <Button onClick={() => navigate('/')} className="mt-4 bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-12 px-8 font-semibold">
          Continuer mes achats
        </Button>
      </div>
    );
  }

  const handlePay = async () => {
    if (!isValid) {
      toast({ title: 'Formulaire incomplet', description: 'Merci de remplir tous les champs obligatoires.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: cart.map((it) => ({ product_id: it.id, quantity: it.quantity })),
        origin_url: window.location.origin,
        shipping: form,
        user_id: user && typeof user === 'object' ? user.user_id : null,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL manquante');
      }
    } catch (e) {
      setLoading(false);
      toast({
        title: 'Erreur de paiement',
        description: e?.response?.data?.detail || 'Impossible de démarrer le paiement. Réessayez.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="checkout-page">
      <nav className="flex items-center gap-1 text-sm text-[#595959] mb-4">
        <Link to="/" className="hover:text-[#00847A]">Accueil</Link>
        <ChevronRight size={14} />
        <Link to="/panier" className="hover:text-[#00847A]">Panier</Link>
        <ChevronRight size={14} />
        <span className="text-[#262626] font-medium">Commande</span>
      </nav>

      <h1 className="text-3xl font-black text-[#262626] mb-6">Finaliser ma commande</h1>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        {/* Shipping form */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <h2 className="font-bold text-lg text-[#262626] mb-4 flex items-center gap-2"><Truck size={18} className="text-[#00847A]" /> Adresse de livraison</h2>
          <p className="text-xs text-[#595959] mb-4">Achat en tant qu'invité — aucune inscription requise.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="full_name">Nom complet *</Label>
              <Input id="full_name" value={form.full_name} onChange={set('full_name')} data-testid="checkout-name" className="mt-1" placeholder="Jean Dupont" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={set('email')} data-testid="checkout-email" className="mt-1" placeholder="jean@exemple.fr" />
            </div>
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input id="phone" value={form.phone} onChange={set('phone')} data-testid="checkout-phone" className="mt-1" placeholder="06 12 34 56 78" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input id="address" value={form.address} onChange={set('address')} data-testid="checkout-address" className="mt-1" placeholder="12 rue des Lilas" />
            </div>
            <div>
              <Label htmlFor="postal_code">Code postal *</Label>
              <Input id="postal_code" value={form.postal_code} onChange={set('postal_code')} data-testid="checkout-postal" className="mt-1" placeholder="75001" />
            </div>
            <div>
              <Label htmlFor="city">Ville *</Label>
              <Input id="city" value={form.city} onChange={set('city')} data-testid="checkout-city" className="mt-1" placeholder="Paris" />
            </div>
          </div>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-neutral-200 rounded-xl p-5 h-fit sticky top-32">
          <h3 className="font-bold text-lg text-[#262626] mb-4">Récapitulatif</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-4">
            {cart.map((item) => (
              <div key={item.id} className="flex gap-3 text-sm">
                <div className="w-12 h-12 shrink-0 bg-neutral-50 rounded overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="line-clamp-1 text-[#262626]">{item.name}</div>
                  <div className="text-xs text-[#595959]">Qté {item.quantity}</div>
                </div>
                <div className="font-semibold text-[#262626]">{(item.price * item.quantity).toFixed(2).replace('.', ',')} €</div>
              </div>
            ))}
          </div>

          <div className="space-y-2 text-sm text-[#262626] border-t border-neutral-200 pt-4">
            <div className="flex justify-between">
              <span>Sous-total</span>
              <span className="font-semibold">{totalPrice.toFixed(2).replace('.', ',')} €</span>
            </div>
            <div className="flex justify-between">
              <span>Livraison</span>
              <span className="font-semibold">{shipping === 0 ? <span className="text-[#00847A]">Gratuite</span> : `${shipping.toFixed(2).replace('.', ',')} €`}</span>
            </div>
          </div>

          <div className="border-t border-neutral-200 my-4 pt-4 flex justify-between items-baseline">
            <span className="font-bold text-[#262626]">Total TTC</span>
            <span className="text-2xl font-black text-[#262626]" data-testid="checkout-total">{total.toFixed(2).replace('.', ',')} €</span>
          </div>

          <Button onClick={handlePay} disabled={loading} data-testid="pay-btn" className="w-full bg-[#FF4D4D] hover:bg-[#E63939] text-white rounded-full h-12 font-semibold text-base disabled:opacity-70">
            {loading ? (
              <><Loader2 size={18} className="mr-2 animate-spin" /> Redirection…</>
            ) : (
              <><Lock size={16} className="mr-2" /> Payer {total.toFixed(2).replace('.', ',')} €</>
            )}
          </Button>

          <div className="mt-4 flex items-center gap-2 text-xs text-[#595959]">
            <Shield size={14} className="text-[#00847A]" /> Paiement 100% sécurisé via Stripe
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
